import { CodingExamStatics, CodingExamExaminerData } from '../../../../../../../store/workspace/types';
import { CheckCircleIcon, TimesCircleIcon } from '../../../../../../../assets/icons';
import { selectFetchingVideoSrc } from '../../../../../../../store/workspace/selectors';
import { getExamFilesThunk } from '../../../../../../../store/workspace/thunks';
import { useDispatch, useSelector } from '../../../../../../../store/store';
import MotionWrapper from '../../../../../../../layouts/MotionWrapper';
import { CodeBlock, dracula } from 'react-code-blocks';
import Button from '../../../../../../../components/Button';
import { useState } from 'react';
import Accordion, { accordionActionClassName } from '../../../../components/Accordion';
import EmptyState from '../../../../components/EmptyState';
import { CODING_QUESTION_INDEXES } from '../../../../constants';
import { useAccordions } from '../../../../hooks/useAccordions';
import { CODING_METRIC_GROUPS, codingScoreOf } from '../../../../codingMetrics';
import styles from './style.module.scss';

const SCORE_COLUMNS = CODING_METRIC_GROUPS.flatMap(group => group.metrics);

interface Props {
    examData: CodingExamStatics | null;
    examinerData?: CodingExamExaminerData;
};

export default function CodingExamResults(props: Props) {
    const { examData, examinerData } = props;

    const dispatch = useDispatch();
    const { isOpen, toggle } = useAccordions();
    const fetchingVideoSrc = useSelector(selectFetchingVideoSrc);
    const [fetchingFile, setFetchingFile] = useState<string | null>(null);

    const languageQuestions = examinerData
        ? examData?.exam?.questions.find(item => examinerData.language in item)
        : undefined;
    const questions = (examinerData && languageQuestions) ? languageQuestions[examinerData.language] : [];

    const getVideo = (fileName: string) => {
        setFetchingFile(fileName);
        dispatch(getExamFilesThunk({
            jobId: examData?.exam?.jobId,
            multipleChoiceExamId: null,
            codingExamId: examData?.exam?.id,
            applicantId: examinerData?.userId,
            file: fileName,
        }));
    };

    const renderPlayerAccordion = (title: string, fileName: string) => (
        <Accordion
            title={title}
            action={<Button
                type='Primary'
                text='Open Player'
                className={accordionActionClassName}
                onClick={() => getVideo(fileName)}
                loading={fetchingVideoSrc && fetchingFile === fileName}
            />}
        />
    );

    if (!examinerData) {
        return (
            <MotionWrapper className={styles.container}>
                <EmptyState className={styles.empty_state} />
            </MotionWrapper>
        );
    }

    return (
        <MotionWrapper className={styles.container}>
            <div className={styles.detail}>
                <div className={styles.header}>
                    <div className={styles.header_top}>
                        <span className={styles.header_top_title}>{examData?.jobInfo.title}</span>
                    </div>
                    <div className={styles.header_bottom}>
                        <span className={styles.header_bottom_item}>
                            Applicant Name : {examinerData.first_name} {examinerData.family_name}
                        </span>
                        <span className={styles.header_bottom_item}>
                            Question Quantities : {examData?.exam?.countQuestions}
                        </span>
                        <span className={styles.header_bottom_item}>
                            Exam Time : {examData?.exam?.time}'
                        </span>
                    </div>
                </div>

                {CODING_QUESTION_INDEXES.map(questionIndex => {
                    const score = examinerData.scores[questionIndex];
                    const label = `Q${questionIndex + 1}`;

                    return (
                        <div className={styles.question_data} key={questionIndex}>
                            <table>
                                <thead>
                                    <tr>
                                        <th colSpan={1}></th>
                                        {CODING_METRIC_GROUPS.map(group => <th key={group.title} colSpan={group.metrics.length}>
                                            <span className={styles.head_section}>{group.title}</span>
                                        </th>)}
                                    </tr>
                                    <tr>
                                        <th>Q</th>
                                        {SCORE_COLUMNS.map(metric => <th key={metric.key}>{metric.title}</th>)}
                                        <th>Verification<br />constraint</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{label}</td>
                                        {SCORE_COLUMNS.map(metric => <td key={metric.key}>
                                            {codingScoreOf(score, metric.key)}/10
                                        </td>)}
                                        <td>{score?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className={styles.card}>
                                <div className={styles.card_body}>
                                    {CODING_METRIC_GROUPS.map(group => <div key={group.title} className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>{group.title}</span>
                                        </div>
                                        <div className={styles.card_body_section_content}>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>{label}</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    {group.metrics.map(metric => <div
                                                        key={metric.key}
                                                        className={styles.card_body_section_content_group_details_item}
                                                    >
                                                        <span>{metric.title}</span>
                                                        <span>{codingScoreOf(score, metric.key)}/10</span>
                                                    </div>)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>)}
                                </div>
                            </div>

                            <Accordion
                                preformatted
                                title='Question Description'
                                open={isOpen(`description-${questionIndex}`)}
                                onToggle={() => toggle(`description-${questionIndex}`)}
                            >
                                {questions[questionIndex]?.description}
                            </Accordion>

                            <Accordion
                                title='Applicant Answer:'
                                open={isOpen(`answer-${questionIndex}`)}
                                onToggle={() => toggle(`answer-${questionIndex}`)}
                            >
                                <CodeBlock
                                    theme={dracula}
                                    showLineNumbers={true}
                                    language={examinerData.language}
                                    text={examinerData.answer.answers[questionIndex]}
                                />
                            </Accordion>

                            <Accordion
                                title='Test Cases:'
                                open={isOpen(`tests-${questionIndex}`)}
                                onToggle={() => toggle(`tests-${questionIndex}`)}
                            >
                                <CodeBlock
                                    theme={dracula}
                                    showLineNumbers={true}
                                    language={examinerData.language}
                                    text={questions[questionIndex]?.test_cases}
                                />
                            </Accordion>

                            <Accordion
                                title='Answer report:'
                                open={isOpen(`report-${questionIndex}`)}
                                onToggle={() => toggle(`report-${questionIndex}`)}
                            >
                                {score?.feedback_report}
                            </Accordion>

                            {renderPlayerAccordion('Applicant Video:', `stream_${questionIndex + 1}.webm`)}
                            {renderPlayerAccordion('Screen Record:', `screen_record_part_${questionIndex + 1}.webm`)}
                        </div>
                    );
                })}
            </div>
        </MotionWrapper>
    );
};
